import { sendError, sendSuccess } from '@components/Noti';
import { API_DOMAIN } from '@constants';
import {
  $categoryList,
  $categoryMap,
  $origSceneIds,
  $origSceneMap,
  $origTourData,
  $sceneMap,
  $selectedPath,
  $tourData,
} from '@stores/dataStores';
import { $isSDK } from '@stores/sdkStores';
import {
  type CategoryData,
  type ESelected,
  FeatureType,
  type GeneralID,
  type IdMaps,
  SandTableType,
  type SceneData,
  type SceneType,
  type TourData,
} from '@types';
import axios from '@utils/axios';
import getTid from '@utils/getTid';
import { teamConfig } from '@utils/teamConfig';
/* eslint-disable no-plusplus */
import { attach } from 'effector';
import produce from 'immer';
import equals from 'ramda/es/equals';
import { shallowEqualObjects } from 'shallow-equal';
import {
  isMapFeatureMarker,
  isSTMarkerScene,
} from '../../sandTable/utils/markerTypeCheck';
import domain from '../domain';
import dataHandler from '../utils/dataHandler';

type Scene = Omit<SceneData, 'id'> & {
  id?: GeneralID;
};

interface NewSceneItem {
  type: SceneType;
  materialId: number;
  tempId: GeneralID;
  categoryId: number;
  sort: number;
}

interface Params {
  data: Partial<TourData>;
  origData: Partial<TourData>;
  category: CategoryData[];
  scenes: Scene[];
  deletedSceneIds: GeneralID[];
  newSceneList: NewSceneItem[];
  selectedPath: ESelected;
}

const saveDataEffect = domain.effect<Params, any>(
  async ({
    data,
    origData,
    category,
    scenes,
    deletedSceneIds,
    newSceneList,
    selectedPath,
  }) => {
    if ($isSDK.getState()) {
      return {};
    }
    let { categoryConfig, customButton, sandBox, config } = data;
    const newSelectedPath = { ...selectedPath };
    const sceneUrl = teamConfig(
      `${API_DOMAIN}/member/my/tour/${getTid()}/scene`,
      `${API_DOMAIN}/team/app/tours/${getTid()}/scene`
    );
    const idMaps = newSceneList.length
      ? await axios.post<IdMaps>(sceneUrl, {
          data: JSON.stringify(newSceneList),
        })
      : null;

    if (idMaps) {
      scenes = produce(scenes, (draft) => {
        draft.forEach((scene) => {
          const map0 = idMaps.find((x) => x.tempId === scene.id);
          if (map0) {
            scene.id = map0.id;
          }
          scene.hotspot?.forEach((spot) => {
            if (spot.data.code === FeatureType.scene) {
              const targetId = spot.data.sceneId;
              const map1 = idMaps.find((x) => x.tempId === targetId);
              if (map1) {
                spot.data.sceneId = map1.id;
              }
            }
          });

          scene.config.components?.forEach((c) => {
            if (c.data.code === FeatureType.scene) {
              const targetId = c.data.sceneId;
              const map3 = idMaps.find((x) => x.tempId === targetId);
              if (map3) {
                c.data.sceneId = map3.id;
              }
            }
          });

          const rotateTargetId = scene.config?.autoRotate?.targetSceneId;
          if (rotateTargetId) {
            const map2 = idMaps.find((x) => x.tempId === rotateTargetId);
            if (map2) {
              scene.config.autoRotate!.targetSceneId = map2.id;
            }
          }
        });
      });

      categoryConfig = produce(data.categoryConfig, (draft) => {
        if (!draft) return;
        const map = idMaps.find((x) => x.tempId === draft.initScene);
        if (map) {
          draft.initScene = map.id;
        }
      });

      customButton = produce(data.customButton, (draft) => {
        if (!draft) return;
        draft.forEach((g) => {
          g.children.forEach((b) => {
            if (b.code === FeatureType.scene) {
              const map = idMaps.find((x) => x.tempId === b.sceneId);
              if (map) {
                b.sceneId = map.id;
              }
            }
          });
        });
      });

      const selectedIdMap = idMaps.find((x) => x.tempId === selectedPath.id);
      if (selectedIdMap) {
        newSelectedPath.id = selectedIdMap.id;
      }

      sandBox = produce(data.sandBox, (draft) => {
        if (!draft) return;
        draft.forEach((s) => {
          s.markers.forEach((m) => {
            if (isSTMarkerScene(m)) {
              const map = idMaps.find((x) => x.tempId === m.data.sceneId);
              if (map) {
                m.data.sceneId = map.id;
              }
            }
          });

          // v5.8.10, 沙盘新增标记点id检查
          if (s.type === SandTableType.map) {
            s.markers.forEach((m) => {
              if (isMapFeatureMarker(m)) {
                const featureData = m.data;
                if (featureData.code === FeatureType.scene) {
                  const map = idMaps.find(
                    (x) => x.tempId === featureData.sceneId
                  );
                  if (map) {
                    featureData.sceneId = map.id;
                  }
                }
              }
            });
          }
        });
      });

      // 处理付费场景
      config = produce(config, (draft) => {
        if (!draft?.vipView?.sceneList) return;
        const list = draft.vipView.sceneList.split(',');
        const newList = list.map((id) => {
          const map = idMaps.find((x) => x.tempId === id);
          return map ? map.id : id;
        });
        draft.vipView.sceneList = newList.join(',');
      });
    }

    const isChanged = (key: keyof TourData) => {
      let result: boolean;
      if (key === 'sandBox') {
        result = !shallowEqualObjects(data.sandBox, origData.sandBox);
      }
      result = !equals(data[key], origData[key]);
      return result;
    };

    const requestData = {
      ...(isChanged('name') && { name: data.name }),
      ...(isChanged('remark') && { remark: data.remark }),
      ...(isChanged('thumbUrl') && { thumbUrl: data.thumbUrl }),
      ...(isChanged('keywords') && { keywords: data.keywords }),
      ...(isChanged('templateId') && { templateId: data.templateId }),
      ...(!equals(categoryConfig, origData.categoryConfig) && {
        categoryConfig: JSON.stringify(categoryConfig),
      }),
      ...(isChanged('logo') && { logo: JSON.stringify(data.logo) }),
      ...(isChanged('flag') && { flag: JSON.stringify(data.flag) }),
      ...(isChanged('enterCover') && {
        enterCover: JSON.stringify(data.enterCover),
      }),
      ...(isChanged('startAnime') && {
        startAnime: JSON.stringify(data.startAnime),
      }),
      ...(isChanged('autoRotate') && {
        autoRotate: JSON.stringify(data.autoRotate),
      }),
      ...(isChanged('tourTip') && { tourTip: JSON.stringify(data.tourTip) }),
      ...(!equals(config, origData.config) && {
        config: JSON.stringify(config),
      }),
      ...(!equals(customButton, origData.customButton) && {
        customButton: JSON.stringify(customButton),
      }),
      ...(!equals(category, origData.category) && {
        category: JSON.stringify(category),
      }),
      ...(!equals(sandBox, origData.sandBox) && {
        sandBox: JSON.stringify(sandBox),
      }),
      scenes: JSON.stringify(scenes),
      ...(deletedSceneIds.length && {
        deletedSceneIds: deletedSceneIds.join(','),
      }),
      ...(isChanged('channelId') && { channelId: data.channelId }),
      ...(data.channelId &&
        isChanged('subChannel') && { subChannel: data.subChannel }),
    };

    const url = teamConfig(
      `${API_DOMAIN}/member/my/tour/${getTid()}`,
      `${API_DOMAIN}/team/app/tours/${getTid()}`
    );

    const newData = await axios.post<TourData>(url, requestData);
    dataHandler(newData, { selectedPath: newSelectedPath });
    return newData;
  }
);

saveDataEffect.done.watch(() => {
  sendSuccess('保存成功');
});

saveDataEffect.failData.watch((e) => {
  sendError(e.message);
});

const saveData = attach({
  source: {
    data: $tourData,
    origData: $origTourData,
    cMap: $categoryMap,
    sMap: $sceneMap,
    origMap: $origSceneMap,
    cList: $categoryList,
    origIds: $origSceneIds,
    selectedPath: $selectedPath,
  },
  effect: saveDataEffect,
  mapParams: (
    _: void,
    { data, origData, cMap, cList, sMap, origMap, origIds, selectedPath }
  ) => {
    const category: CategoryData[] = [];
    let scenes: Scene[] = [];
    const newSceneList: NewSceneItem[] = [];

    for (let i = 0; i < cList.length; i++) {
      const cId = cList[i];
      const cItem = cMap.get(cId)!;
      if (cItem.categoryIds.length) {
        const children: CategoryData[] = [];
        for (let j = 0; j < cItem.categoryIds.length; j++) {
          const subId = cItem.categoryIds[j];
          const subItem = cMap.get(subId)!;
          let sort = 0;
          for (let k = 0; k < subItem.sceneIds.length; k++) {
            const sceneItem = sMap.get(subItem.sceneIds[k]);
            if (sceneItem) {
              sort++;
              scenes.push({ ...sceneItem, categoryId: subId, sort });
              if (!origIds.includes(sceneItem.id)) {
                newSceneList.push({
                  type: sceneItem.type,
                  categoryId: subId,
                  sort,
                  materialId: sceneItem.materialId,
                  tempId: sceneItem.id,
                });
              }
            }
          }
          children.push({ id: subId, name: subItem.name });
        }
        category.push({ id: cId, name: cItem.name, children });
      } else {
        let sort = 0;
        for (let m = 0; m < cItem.sceneIds.length; m++) {
          const sceneItem = sMap.get(cItem.sceneIds[m]);
          if (sceneItem) {
            sort++;
            scenes.push({ ...sceneItem, categoryId: cId, sort });
            if (!origIds.includes(sceneItem.id)) {
              newSceneList.push({
                type: sceneItem.type,
                categoryId: cId,
                sort,
                materialId: sceneItem.materialId,
                tempId: sceneItem.id,
              });
            }
          }
        }
        category.push({ id: cId, name: cItem.name });
      }
    }
    const deletedSceneIds = origIds.filter((_id) => !sMap.has(_id));

    scenes = scenes.filter((x) => {
      const origItem = origMap.get(x.id as GeneralID);
      if (!origItem) {
        return true;
      }
      if (!shallowEqualObjects(x, origItem)) {
        return true;
      }
      return false;
    });

    return {
      data,
      origData,
      category,
      scenes,
      deletedSceneIds,
      newSceneList,
      selectedPath,
    };
  },
});

export default saveData;
